import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, FileSpreadsheet, FileText, Filter } from 'lucide-react';
import { ColumnSelector } from './ColumnSelector';
import { ExportProgress } from './ExportProgress';
import { useLeadExport } from '@/hooks/useLeadExport';
import { ExportConfig, EXPORT_COLUMN_GROUPS } from '@/types/import';
import { LeadFilters } from '@/types/filters';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentFilters?: LeadFilters;
  totalLeads?: number;
}

type ExportFormat = 'csv' | 'xlsx';

export const ExportModal: React.FC<ExportModalProps> = ({
  isOpen,
  onClose,
  currentFilters = {},
  totalLeads = 0,
}) => {
  const { startExport, downloadExport, getJobStatus, isExporting, error } = useLeadExport();

  // Form state
  const [format, setFormat] = useState<ExportFormat>('csv');
  const [selectedColumns, setSelectedColumns] = useState<string[]>(() => {
    // Default to all basic and contact columns
    return [
      ...EXPORT_COLUMN_GROUPS.basic.columns.map(c => c.value),
      ...EXPORT_COLUMN_GROUPS.contact.columns.map(c => c.value),
      ...EXPORT_COLUMN_GROUPS.deal.columns.map(c => c.value),
    ];
  });
  const [jobId, setJobId] = useState<string | null>(null);
  const [showProgress, setShowProgress] = useState(false);

  // Check if filters are active
  const hasActiveFilters = Object.keys(currentFilters).some(key => {
    const value = currentFilters[key as keyof LeadFilters];
    if (Array.isArray(value)) return value.length > 0;
    return value !== undefined && value !== '';
  });

  // Start export
  const handleExport = async () => {
    if (selectedColumns.length === 0) return;

    try {
      const config: ExportConfig = {
        format,
        columns: selectedColumns,
        filters: {
          stage: currentFilters.stages,
          priority: currentFilters.priorities,
          assignedTo: currentFilters.assignedTo,
          createdAfter: currentFilters.entryDateFrom,
          createdBefore: currentFilters.entryDateTo,
          minValue: currentFilters.dealValueMin,
          maxValue: currentFilters.dealValueMax,
        },
      };

      const result = await startExport(config);
      setJobId(result.jobId);
      setShowProgress(true);
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const handleDownload = (jobId: string) => {
    downloadExport(jobId);
  };

  const handleClose = () => {
    setShowProgress(false);
    setJobId(null);
    onClose();
  };

  const formatOptions = [
    {
      value: 'csv' as const,
      label: 'CSV',
      description: 'Comma-separated values',
      icon: FileText,
    },
    {
      value: 'xlsx' as const,
      label: 'Excel',
      description: 'Microsoft Excel format',
      icon: FileSpreadsheet,
    },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <Card className="p-6 bg-white shadow-2xl">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                    <Download className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Export Leads</h2>
                    <p className="text-sm text-gray-600">
                      {totalLeads > 0 ? `Export ${totalLeads.toLocaleString()} leads` : 'Export your leads'}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {showProgress && jobId ? (
                <ExportProgress
                  jobId={jobId}
                  getJobStatus={getJobStatus}
                  onDownload={handleDownload}
                  onClose={handleClose}
                />
              ) : (
                <div className="space-y-6">
                  {/* Filter summary */}
                  {hasActiveFilters && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center space-x-2 text-blue-700">
                        <Filter className="w-4 h-4" />
                        <span className="text-sm font-medium">Filters Active</span>
                      </div>
                      <p className="text-sm text-blue-600 mt-1">
                        Your export will include only leads matching your current filters.
                      </p>
                    </div>
                  )}

                  {/* Format selection */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Export Format</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {formatOptions.map((option) => {
                        const Icon = option.icon;
                        const isSelected = format === option.value;

                        return (
                          <button
                            key={option.value}
                            onClick={() => setFormat(option.value)}
                            className={cn(
                              'flex items-center space-x-3 p-4 border rounded-lg transition-colors text-left',
                              isSelected
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            )}
                          >
                            <div className={cn(
                              'w-10 h-10 rounded-lg flex items-center justify-center',
                              isSelected ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'
                            )}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <div>
                              <p className={cn(
                                'text-sm font-medium',
                                isSelected ? 'text-blue-900' : 'text-gray-900'
                              )}>
                                {option.label}
                              </p>
                              <p className={cn(
                                'text-xs',
                                isSelected ? 'text-blue-600' : 'text-gray-500'
                              )}>
                                {option.description}
                              </p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Column selection */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Select Columns</h4>
                    <div className="max-h-64 overflow-y-auto border rounded-lg p-4">
                      <ColumnSelector
                        selectedColumns={selectedColumns}
                        onChange={setSelectedColumns}
                      />
                    </div>
                  </div>

                  {/* Error message */}
                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-red-600 text-sm">{error}</p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-4 border-t">
                    <p className="text-sm text-gray-500">
                      {selectedColumns.length} columns selected
                    </p>

                    <div className="flex items-center space-x-3">
                      <Button variant="outline" onClick={handleClose}>
                        Cancel
                      </Button>
                      <Button
                        onClick={handleExport}
                        disabled={selectedColumns.length === 0 || isExporting}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {isExporting ? (
                          <div className="flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                            Exporting...
                          </div>
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-2" />
                            Export
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
